import { normaliseSeed, SeededRandom, stableFingerprint } from './random.js';
import { DEFAULT_DUNGEON_ENTRANCE_RADII, DungeonEntranceError, } from './entrance-types.js';
import { validateDungeonEntranceSite, validatePlaceDungeonEntrancesRequest, } from './entrance-validate.js';
function weightedOrder(anchors, random) {
    return anchors
        .map((anchor) => ({
        anchor,
        // Weighted sampling without replacement: lower exponential key wins.
        key: -Math.log(Math.max(Number.EPSILON, random.next())) / anchor.selectionWeight,
    }))
        .sort((left, right) => left.key - right.key || left.anchor.id.localeCompare(right.anchor.id))
        .map((entry) => entry.anchor);
}
function allocateAnchors(slots, anchors, random) {
    const orders = new Map();
    for (const slot of slots) {
        orders.set(slot, weightedOrder(anchors.filter((anchor) => anchor.allowedDifficultyIds.includes(slot.difficultyId)), random));
    }
    const used = new Set();
    const assigned = new Map();
    const visit = (index) => {
        if (index >= slots.length)
            return true;
        const slot = slots[index];
        for (const anchor of orders.get(slot) ?? []) {
            if (used.has(anchor.id))
                continue;
            used.add(anchor.id);
            assigned.set(slot, anchor);
            if (visit(index + 1))
                return true;
            assigned.delete(slot);
            used.delete(anchor.id);
        }
        return false;
    };
    return visit(0) ? assigned : null;
}
function materialForSite(anchor, difficultyId, radii) {
    return {
        anchorId: anchor.id,
        biomeId: anchor.biomeId,
        position: { ...anchor.position },
        rotationQuarterTurns: anchor.rotationQuarterTurns,
        clearanceRadius: anchor.clearanceRadius,
        targetTier: anchor.targetTier,
        difficultyId,
        themeIds: [...anchor.themeIds].sort(),
        radii: { ...(radii ?? DEFAULT_DUNGEON_ENTRANCE_RADII) },
    };
}
/**
 * Deterministically selects terrain-safe anchors and permanently assigns each a
 * difficulty. It never generates arbitrary world coordinates.
 */
export function placeDungeonEntranceSites(request, anchors, profiles, catalog) {
    const problems = validatePlaceDungeonEntrancesRequest(request, anchors, profiles, catalog);
    if (problems.length > 0)
        throw new DungeonEntranceError(problems);
    const profileByDifficulty = new Map(profiles.map((profile) => [profile.difficultyId, profile]));
    const slots = request.difficultyQuotas.flatMap((quota) => {
        const profile = profileByDifficulty.get(quota.difficultyId);
        const eligibleAnchorCount = anchors.filter((anchor) => anchor.allowedDifficultyIds.includes(quota.difficultyId)).length;
        return Array.from({ length: quota.count }, (_, slotIndex) => ({
            difficultyId: quota.difficultyId,
            slotIndex,
            profileRank: profile.rank,
            eligibleAnchorCount,
        }));
    });
    slots.sort((left, right) => left.eligibleAnchorCount - right.eligibleAnchorCount ||
        right.profileRank - left.profileRank ||
        left.difficultyId.localeCompare(right.difficultyId) ||
        left.slotIndex - right.slotIndex);
    const seed = `entrance-placement::${normaliseSeed(request.worldSeed)}`;
    const allocation = allocateAnchors(slots, anchors, new SeededRandom(seed));
    if (!allocation) {
        throw new DungeonEntranceError([
            {
                code: 'anchor-allocation-failed',
                path: 'request.difficultyQuotas',
                message: 'no distinct-anchor allocation satisfies all difficulty quotas',
            },
        ]);
    }
    const sites = slots.map((slot) => {
        const anchor = allocation.get(slot);
        const material = materialForSite(anchor, slot.difficultyId, request.radii);
        const placementFingerprint = stableFingerprint(material);
        return {
            id: `dungeon-entrance-${anchor.id}`,
            ...material,
            placementFingerprint,
        };
    });
    sites.sort((left, right) => (profileByDifficulty.get(left.difficultyId)?.rank ?? 0) -
        (profileByDifficulty.get(right.difficultyId)?.rank ?? 0) ||
        left.anchorId.localeCompare(right.anchorId));
    const siteProblems = sites.flatMap((site) => validateDungeonEntranceSite(site, profileByDifficulty.get(site.difficultyId)));
    if (siteProblems.length > 0)
        throw new DungeonEntranceError(siteProblems);
    return sites;
}
export function initialiseDungeonEntranceWorld(sites) {
    const states = {};
    for (const site of sites) {
        states[site.id] = { phase: 'dormant', siteId: site.id, generationCycle: 0 };
    }
    return { sites: states };
}
//# sourceMappingURL=entrance-placement.js.map